// /app/admin/health — live system health board.
//
// Polls nexus-healthcheck's /status every 30 seconds and renders one card
// per endpoint check. Cards group by severity (critical first) so the
// most impactful failures jump out. Manual refresh button forces a
// re-fetch. Auto-pause when the tab is hidden so we don't burn requests
// on a backgrounded tab.

import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { useI18n } from "../../../lib/i18n";
import {
  ApiError,
  healthcheck,
  type HealthcheckCheck,
  type HealthcheckStatus,
} from "../../../lib/api";

const POLL_MS = 30_000;

function HealthBoard() {
  const { t } = useI18n();
  const [data, setData] = useState<HealthcheckStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await healthcheck.status();
      setData(r);
      setErr(null);
      setLastFetched(new Date());
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + polling. Auto-pause on hidden tab so we don't keep burning
  // requests when the user has tabbed away. Resume on visibility change.
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (!document.hidden) {
        await fetchOnce();
      }
      if (!cancelled) {
        timer = window.setTimeout(tick, POLL_MS);
      }
    };
    void tick();

    const onVis = () => {
      // When the tab comes back into view, fetch once immediately so the
      // user doesn't stare at stale data for up to POLL_MS seconds.
      if (!document.hidden) void fetchOnce();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchOnce]);

  // Group by severity for layout: critical first, then warning, then info.
  // Within each group, failed > succeeded so red cards land at the top.
  const grouped = useMemo(() => {
    const empty = {
      critical: [] as HealthcheckCheck[],
      warning:  [] as HealthcheckCheck[],
      info:     [] as HealthcheckCheck[],
    };
    if (!data) return empty;
    for (const c of data.checks) {
      empty[c.severity].push(c);
    }
    for (const k of ["critical", "warning", "info"] as const) {
      empty[k].sort((a, b) => Number(a.ok) - Number(b.ok));
    }
    return empty;
  }, [data]);

  const overall = data?.ok ?? null;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      {/* Header + overall pill */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2 min-w-0">
          <span className="eyebrow">{t("admin.health.eyebrow")}</span>
          <h1 className="font-display text-3xl font-semibold">{t("admin.health.title")}</h1>
          <p className="text-xs text-muted">
            {t("admin.health.subtitle")}
            {lastFetched ? (
              <>
                {" · "}
                <span className="font-mono">{t("admin.health.lastFetched")}: {lastFetched.toLocaleTimeString()}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <OverallPill ok={overall} />
          <Button variant="secondary" onClick={() => void fetchOnce()} disabled={loading}>
            {t("admin.health.refresh")}
          </Button>
        </div>
      </div>

      {/* Summary counters */}
      {data ? (
        <Card>
          <dl className="grid sm:grid-cols-4 gap-x-6 gap-y-3">
            <Counter label={t("admin.health.total")} value={data.total} />
            <Counter label={t("admin.health.passing")} value={data.total - data.failed} tone="success" />
            <Counter label={t("admin.health.failing")} value={data.failed} tone={data.failed > 0 ? "warning" : undefined} />
            <Counter label={t("admin.health.criticalFailing")} value={data.critical_failed} tone={data.critical_failed > 0 ? "error" : undefined} />
          </dl>
        </Card>
      ) : null}

      {err && !data ? (
        <Card>
          <p className="text-error text-sm" role="alert">{err}</p>
        </Card>
      ) : null}

      {!data && loading ? (
        <Card>
          <p className="text-secondary text-sm">{t("common.loading")}</p>
        </Card>
      ) : null}

      {data ? (
        <>
          {grouped.critical.length > 0 ? (
            <Section title={t("admin.health.section.critical")} checks={grouped.critical} />
          ) : null}
          {grouped.warning.length > 0 ? (
            <Section title={t("admin.health.section.warning")} checks={grouped.warning} />
          ) : null}
          {grouped.info.length > 0 ? (
            <Section title={t("admin.health.section.info")} checks={grouped.info} />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function OverallPill({ ok }: { ok: boolean | null }) {
  const { t } = useI18n();
  if (ok == null) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-border-default px-3 py-1 text-xs font-mono uppercase tracking-[0.16em] text-muted">
        <span className="inline-block h-2 w-2 rounded-full bg-muted" aria-hidden />
        {t("admin.health.overall.unknown")}
      </span>
    );
  }
  if (ok) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-success/40 bg-success/10 px-3 py-1 text-xs font-mono uppercase tracking-[0.16em] text-success">
        <span className="inline-block h-2 w-2 rounded-full bg-success" aria-hidden />
        {t("admin.health.overall.ok")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-error/40 bg-error/10 px-3 py-1 text-xs font-mono uppercase tracking-[0.16em] text-error">
      <span className="inline-block h-2 w-2 rounded-full bg-error animate-pulse" aria-hidden />
      {t("admin.health.overall.down")}
    </span>
  );
}

function Counter({
  label, value, tone,
}: {
  label: string;
  value: number;
  tone?: "success" | "warning" | "error";
}) {
  const toneClass =
    tone === "success" ? "text-success"
      : tone === "warning" ? "text-warning"
      : tone === "error" ? "text-error"
      : "text-primary";
  return (
    <div className="flex flex-col gap-1">
      <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">{label}</dt>
      <dd className={`font-display text-2xl font-semibold ${toneClass}`}>{value}</dd>
    </div>
  );
}

function Section({ title, checks }: { title: string; checks: HealthcheckCheck[] }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {checks.map((c) => (
          <li key={c.check}>
            <CheckCard check={c} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function CheckCard({ check: c }: { check: HealthcheckCheck }) {
  const { t } = useI18n();
  const cardTone =
    c.ok ? "border-success/30 bg-success/[0.03]"
      : c.severity === "critical" ? "border-error/40 bg-error/[0.04]"
      : "border-warning/40 bg-warning/[0.04]";
  const mark = c.ok ? "✓" : "✕";
  const markTone =
    c.ok ? "text-success" : c.severity === "critical" ? "text-error" : "text-warning";

  return (
    <Card className={`!p-4 border ${cardTone} transition`}>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className={`font-mono text-sm font-semibold ${markTone}`}>{mark} {c.check}</span>
        <span className="text-[11px] font-mono text-muted">{c.latency_ms}ms</span>
      </div>
      <p className="text-xs text-secondary mb-2 line-clamp-2">{c.description}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-mono text-muted">HTTP {c.http_status || "—"}</span>
        <SeverityBadge sev={c.severity} />
      </div>
      {!c.ok && c.error ? (
        <p className="text-[11px] text-error mt-2 break-words" title={c.error}>
          {c.error.length > 140 ? c.error.slice(0, 140) + "…" : c.error}
        </p>
      ) : null}
      <p className="text-[10px] text-muted font-mono mt-2">
        {t("admin.health.checked")}: {new Date(c.timestamp).toLocaleTimeString()}
      </p>
    </Card>
  );
}

function SeverityBadge({ sev }: { sev: HealthcheckCheck["severity"] }) {
  const { t } = useI18n();
  const tone =
    sev === "critical" ? "border-error/40 text-error"
      : sev === "warning" ? "border-warning/40 text-warning"
      : "border-border-default text-muted";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide ${tone}`}>
      {t(`admin.health.severity.${sev}` as const)}
    </span>
  );
}

export const Route = createFileRoute("/app/admin/health")({
  component: HealthBoard,
});
